"use client"

// Redy admin design system — ToggleGroup.
//
// Elegir 1 de N opciones mutuamente excluyentes. El DS no tenía primitivo para
// esto, así que 26 sitios lo reimplementaron a mano con
// `<button type="button" className={cond ? "bg-…" : "bg-…"}>` — el anti-pattern
// que RULES.md §4 rechaza automáticamente. Inventario completo del barrido en
// docs/design-system/MIGRATION_LOG.md.
//
// No confundir con:
//   <Tabs>   — cambiar de VISTA (role="tablist" + paneles). 12 sitios más son eso.
//   <Toggle> — un booleano on/off (role="switch").
//
// Accesibilidad: implementa el patrón WAI-ARIA de radio group, que es lo que
// "elegir 1 de N" significa para un lector de pantalla. Ninguno de los sitios
// hechos a mano la tenía: 42 de los 46 botones del barrido no declaraban ni
// `aria-pressed` ni `role="radio"`, así que para un usuario de teclado o de
// lector de pantalla eran N botones sueltos sin relación entre sí.
//
// El patrón exige las tres piezas juntas — poner `role="radio"` sin las otras
// dos deja al usuario peor que antes, porque el lector anuncia "radio, 1 de 3"
// y luego las flechas no hacen nada:
//   1. role="radiogroup" con nombre accesible (obligatorio por tipos, ver A11y)
//   2. role="radio" + aria-checked por opción
//   3. roving tabindex — el grupo es UNA parada de tabulación, y dentro se
//      navega con flechas (la selección sigue al foco, como un radio nativo)

import { useRef } from "react"
import type { ReactNode } from "react"

export type ToggleGroupVariant = "track" | "pills" | "cards"
export type ToggleGroupSize = "sm" | "md"

export type ToggleGroupOption<T extends string> = {
  value: T
  label: ReactNode
  /** Segunda línea descriptiva. Solo se pinta en variant="cards". */
  hint?: ReactNode
  /** Icono a la izquierda del label. */
  icon?: ReactNode
  disabled?: boolean
}

/**
 * Un radiogroup sin nombre accesible se anuncia como "grupo" a secas. Es el
 * error más fácil de cometer y el más difícil de ver, así que lo exige el
 * compilador en vez de un comentario que nadie lee.
 */
type A11y =
  | { "aria-label": string; "aria-labelledby"?: never }
  | { "aria-labelledby": string; "aria-label"?: never }

export type ToggleGroupProps<T extends string> = {
  options: readonly ToggleGroupOption<T>[]
  value: T
  onChange: (value: T) => void
  /**
   * track — control segmentado embebido en un carril con fondo (filtros densos).
   * pills — píldoras sueltas con gradiente al seleccionar (con o sin icono).
   * cards — cajas con borde y `hint` opcional en segunda línea (decisiones de formulario).
   */
  variant?: ToggleGroupVariant
  size?: ToggleGroupSize
  /** Reparte las opciones a lo ancho del contenedor (flex-1 cada una). */
  fullWidth?: boolean
  /** Deshabilita el grupo entero. Para una sola opción usa `option.disabled`. */
  disabled?: boolean
  className?: string
} & A11y

// ── Navegación por teclado ──────────────────────────────────────────────────
// Extraída como función pura: el repo corre jest con testEnvironment "node" y
// sin jsdom, así que esta es la única forma de testear el teclado de verdad en
// vez de asumir que funciona. Ver __tests__/ui/toggleGroupKeyboard.test.ts.

const NAV_KEYS = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End"] as const

/**
 * Índice de la siguiente opción habilitada a partir de `from`, avanzando en
 * `step` y dando la vuelta. Devuelve `null` si no hay ninguna habilitada — sin
 * ese corte, un grupo entero deshabilitado colgaría el bucle.
 */
export function nextEnabledIndex(
  options: readonly { disabled?: boolean }[],
  from: number,
  step: 1 | -1,
): number | null {
  const n = options.length
  if (n === 0) return null
  for (let i = 1; i <= n; i++) {
    // `from` puede ser -1 (nada seleccionado); el módulo lo normaliza.
    const idx = (((from + step * i) % n) + n) % n
    if (!options[idx].disabled) return idx
  }
  return null
}

/**
 * Índice destino para una tecla de navegación, o `null` si la tecla no navega
 * (y entonces el evento debe seguir su curso — no tragarse Tab ni Enter).
 */
export function resolveArrowTarget(
  key: string,
  options: readonly { disabled?: boolean }[],
  currentIndex: number,
): number | null {
  switch (key) {
    case "ArrowRight":
    case "ArrowDown":
      return nextEnabledIndex(options, currentIndex, 1)
    case "ArrowLeft":
    case "ArrowUp":
      return nextEnabledIndex(options, currentIndex, -1)
    // Home/End buscan hacia adentro desde el borde para no aterrizar en una
    // opción deshabilitada.
    case "Home":
      return nextEnabledIndex(options, -1, 1)
    case "End":
      return nextEnabledIndex(options, options.length, -1)
    default:
      return null
  }
}

// ── Estilos ─────────────────────────────────────────────────────────────────
// La escala se alinea a la de <Button> para que un ToggleGroup y un Button
// contiguos midan lo mismo.

const SIZE_CLS: Record<ToggleGroupSize, string> = {
  sm: "px-3 py-1.5 text-[13px] font-semibold min-h-[32px] rounded-[8px]",
  md: "px-4 py-2.5 text-sm font-semibold min-h-[40px] rounded-[10px]",
}

/** Radio del carril = radio del item + su padding (p-1 = 4px). */
const TRACK_CLS: Record<ToggleGroupSize, string> = {
  sm: "p-1 rounded-[12px]",
  md: "p-1 rounded-[14px]",
}

const HINT_CLS: Record<ToggleGroupSize, string> = {
  sm: "text-[10.5px]",
  md: "text-[11px]",
}

const ITEM_BASE =
  "inline-flex items-center justify-center gap-1.5 transition-all duration-150 ease-out " +
  "cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--redy-red)]"

const CONTAINER_CLS: Record<ToggleGroupVariant, string> = {
  track:
    "inline-flex gap-1 bg-[var(--redy-warm-white)] border border-[var(--redy-border)]",
  pills: "inline-flex flex-wrap gap-2",
  cards: "flex gap-2",
}

/** Sin fondo seleccionado: el gradiente es un `style`, no una clase. */
const STATE_CLS: Record<ToggleGroupVariant, { on: string; off: string }> = {
  track: {
    on: "text-white",
    off: "text-[var(--redy-secondary)] hover:text-[var(--redy-charcoal)]",
  },
  pills: {
    on: "text-white border border-transparent",
    off:
      "bg-[var(--redy-surface)] text-[var(--redy-secondary)] border border-[var(--redy-border)] " +
      "hover:text-[var(--redy-charcoal)]",
  },
  cards: {
    on: "border border-[var(--redy-red)] bg-[var(--redy-surface-2)] text-[var(--redy-charcoal)]",
    off:
      "border border-[var(--redy-border)] text-[var(--redy-muted)] " +
      "hover:border-[var(--redy-muted)] hover:text-[var(--redy-secondary)]",
  },
}

export function ToggleGroup<T extends string>(props: ToggleGroupProps<T>) {
  const {
    options,
    value,
    onChange,
    variant = "track",
    size = "md",
    fullWidth = false,
    disabled = false,
    className = "",
  } = props

  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  const selectedIndex = options.findIndex((o) => o.value === value)

  // El grupo es UNA parada de tabulación. Si `value` no casa con ninguna opción
  // (estado inicial vacío, o un valor de la BD que ya no existe), la primera
  // habilitada recibe el foco para que el grupo no quede inalcanzable con Tab.
  const tabbableIndex =
    selectedIndex >= 0 && !options[selectedIndex]?.disabled
      ? selectedIndex
      : (nextEnabledIndex(options, -1, 1) ?? -1)

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    if (disabled) return
    if (!(NAV_KEYS as readonly string[]).includes(e.key)) return

    const target = resolveArrowTarget(e.key, options, index)
    if (target === null || target === index) return

    // Solo ahora: si no había destino, el evento sigue su curso normal.
    e.preventDefault()
    onChange(options[target].value)
    itemRefs.current[target]?.focus()
  }

  return (
    <div
      role="radiogroup"
      aria-label={props["aria-label"]}
      aria-labelledby={props["aria-labelledby"]}
      className={[
        CONTAINER_CLS[variant],
        variant === "track" ? TRACK_CLS[size] : "",
        fullWidth ? "w-full" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {options.map((opt, index) => {
        const selected = opt.value === value
        const isDisabled = disabled || !!opt.disabled
        const showHint = variant === "cards" && opt.hint != null

        return (
          <button
            key={opt.value}
            ref={(el) => {
              itemRefs.current[index] = el
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={index === tabbableIndex ? 0 : -1}
            disabled={isDisabled}
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={[
              ITEM_BASE,
              SIZE_CLS[size],
              selected ? STATE_CLS[variant].on : STATE_CLS[variant].off,
              fullWidth || variant === "cards" ? "flex-1" : "",
              showHint ? "flex-col !items-start gap-0.5 text-left" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={
              // El gradiente es la marca del estado seleccionado en todo el DS
              // (Button primary, Toggle, chips del wizard). `cards` es la
              // excepción deliberada: son cajas grandes y el gradiente a página
              // completa las convierte en un bloque rojo.
              selected && variant !== "cards"
                ? { background: "var(--redy-gradient)" }
                : undefined
            }
          >
            {showHint ? (
              <>
                <span className="inline-flex items-center gap-1.5">
                  {opt.icon}
                  {opt.label}
                </span>
                <span
                  className={[
                    HINT_CLS[size],
                    "font-medium",
                    selected ? "text-[var(--redy-secondary)]" : "text-[var(--redy-faint)]",
                  ].join(" ")}
                >
                  {opt.hint}
                </span>
              </>
            ) : (
              <>
                {opt.icon}
                {opt.label}
              </>
            )}
          </button>
        )
      })}
    </div>
  )
}
